import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { ShipmentBatchView, ShipmentList, ShipmentListItem } from "@cx/contracts";
import {
  MAX_BATCH_SHIPMENTS,
  MAX_TRACK_SELECTION,
  SHIPMENT_STATUSES,
  formatBRL,
  isTrackableShipment,
} from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { dateTimeBR } from "../invoices/InvoiceEvents";
import { SHIPMENT_STATUS_TONE, kgLabel, providerLabel, statusLabel } from "./labels";

/** Objeto com etiqueta, ainda não postado e fora de remessa: entra numa remessa (antiga PLP). */
export const batchable = (s: Pick<ShipmentListItem, "status" | "batchId" | "isReverse">) =>
  s.status === "label_ready" && !s.batchId && !s.isReverse;

/**
 * Vendas › Logística (T3.4, RF-VEN-10): objetos de postagem (envios e reversas) com situação, rastreio e frete;
 * sincronizar o rastreio dos selecionados e montar uma remessa com os prontos.
 */
export function ShipmentsPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { situacao } = useSearch({ from: "/vendas/logistica" });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const list = useInfiniteQuery({
    queryKey: ["logistics", companyId, "shipments", situacao ?? ""],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<ShipmentList>(
        `/logistics/shipments?${new URLSearchParams({
          ...(situacao ? { status: situacao } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const summary = list.data?.pages[0]?.summary;
  const chosen = rows.filter((r) => selected.has(r.id));
  const trackIds = chosen.filter((r) => isTrackableShipment(r.status)).map((r) => r.id);
  const batchRows = chosen.filter(batchable);
  const batchIntegrations = new Set(batchRows.map((r) => r.integrationId));
  const onError = (e: Error) => toast("danger", errorMessage(e));
  const track = useMutation({
    mutationFn: () =>
      api<{ queued: number }>("/logistics/shipments/track", {
        method: "POST",
        body: JSON.stringify(trackIds.length ? { ids: trackIds } : {}),
      }),
    onSuccess: () => {
      toast("success", "Rastreio solicitado. As situações se atualizam em instantes.");
      setSelected(new Set());
      setTimeout(() => void qc.invalidateQueries({ queryKey: ["logistics", companyId] }), 3000);
    },
    onError,
  });
  const [batchKey, setBatchKey] = useState(() => crypto.randomUUID());
  const batch = useMutation({
    mutationFn: () =>
      api<ShipmentBatchView>("/logistics/batches", {
        method: "POST",
        headers: { "Idempotency-Key": batchKey },
        body: JSON.stringify({
          integrationId: batchRows[0]!.integrationId,
          shipmentIds: batchRows.map((r) => r.id),
          notes: null,
        }),
      }),
    onSuccess: (b) => {
      setBatchKey(crypto.randomUUID());
      toast("success", `Remessa ${b.number} criada`);
      void qc.invalidateQueries({ queryKey: ["logistics", companyId] });
      void navigate({ to: "/vendas/logistica/remessas/$id", params: { id: b.id } });
    },
    onError,
  });
  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const canUpdate = can(perms, "logistics", "update");
  const canCreate = can(perms, "logistics", "create");
  const batchProblem =
    batchRows.length === 0
      ? "Selecione objetos com etiqueta gerada e fora de remessa"
      : batchIntegrations.size > 1
        ? "Os objetos precisam ser da mesma integração"
        : batchRows.length > MAX_BATCH_SHIPMENTS
          ? `Até ${MAX_BATCH_SHIPMENTS} objetos por remessa`
          : null;
  return (
    <>
      <ContentHeader
        title="Logística"
        breadcrumb={["Vendas", "Logística"]}
        actions={
          <div className="d-flex flex-wrap gap-2">
            <Link to="/vendas/logistica/remessas" className="btn btn-outline-secondary">
              Remessas
            </Link>
            {canUpdate && (
              <button
                type="button"
                className="btn btn-outline-primary"
                disabled={track.isPending || trackIds.length > MAX_TRACK_SELECTION}
                title={trackIds.length ? undefined : "Sem seleção: todos os objetos em andamento"}
                onClick={() => track.mutate()}
              >
                Atualizar rastreio{trackIds.length ? ` (${trackIds.length})` : ""}
              </button>
            )}
            {canCreate && (
              <button
                type="button"
                className="btn btn-primary"
                disabled={!!batchProblem || batch.isPending}
                title={batchProblem ?? undefined}
                onClick={() => batch.mutate()}
              >
                Criar remessa{batchRows.length ? ` (${batchRows.length})` : ""}
              </button>
            )}
          </div>
        }
      />
      <div className="box card">
        <div className="row g-2 p-3 pb-0">
          <div className="col-md-4">
            <label className="visually-hidden" htmlFor="lg-status">
              Situação
            </label>
            <select
              id="lg-status"
              className="form-select"
              value={situacao ?? ""}
              onChange={(e) => {
                setSelected(new Set());
                void navigate({
                  to: "/vendas/logistica",
                  search: { situacao: (e.target.value || undefined) as typeof situacao },
                });
              }}
            >
              <option value="">Todas as situações</option>
              {SHIPMENT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {statusLabel(s)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <EmptyState
            title="Nenhum objeto de postagem"
            description="Gere a etiqueta no pedido de venda (painel Envios) depois de conectar os Correios ou o Melhor Envio."
            actions={[]}
          />
        )}
        {rows.length > 0 && (
          <>
            <div className="table-responsive">
              <table className="table align-middle mb-0" aria-label="Objetos de postagem">
                <thead>
                  <tr>
                    <th scope="col">
                      <span className="visually-hidden">Selecionar</span>
                    </th>
                    <th scope="col">Objeto</th>
                    <th scope="col">Pedido</th>
                    <th scope="col">Destinatário</th>
                    <th scope="col">Serviço</th>
                    <th scope="col">Rastreio</th>
                    <th scope="col" className="text-end">
                      Frete
                    </th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          aria-label={`Selecionar objeto ${r.number}`}
                          checked={selected.has(r.id)}
                          onChange={() => toggle(r.id)}
                        />
                      </td>
                      <td className="text-nowrap">
                        <Link to="/vendas/logistica/$id" params={{ id: r.id }}>
                          {r.number}
                        </Link>
                        {r.isReverse && <span className="badge text-bg-secondary ms-2">Reversa</span>}
                        {r.batchNumber !== null && (
                          <div className="small text-muted">Remessa {r.batchNumber}</div>
                        )}
                      </td>
                      <td>
                        {r.salesOrderId ? (
                          <Link to="/vendas/pedidos/$id" params={{ id: r.salesOrderId }}>
                            {r.salesOrderNumber}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {r.recipientName}
                        <div className="small text-muted">
                          {r.recipientCity}/{r.recipientUf} · {kgLabel(r.weightG)}
                        </div>
                      </td>
                      <td>
                        {r.carrierName ? `${r.carrierName} ` : ""}
                        {r.serviceName}
                        <div className="small text-muted">
                          {r.integrationName} · {providerLabel(r.provider)}
                        </div>
                      </td>
                      <td className="font-monospace small">{r.trackingCode ?? "—"}</td>
                      <td className="text-end">
                        {r.freightCostCents === null ? "—" : formatBRL(r.freightCostCents)}
                      </td>
                      <td>
                        <StatusBadge tone={SHIPMENT_STATUS_TONE[r.status]}>
                          {statusLabel(r.status, r.isReverse)}
                        </StatusBadge>
                        {r.lastEventAt && <div className="small text-muted">{dateTimeBR(r.lastEventAt)}</div>}
                        {r.status === "error" && r.errorMessage && (
                          <div className="small text-danger">{r.errorMessage}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-3">
              {summary && (
                <span className="text-muted small" data-testid="shipments-summary">
                  {summary.count} {summary.count === 1 ? "objeto" : "objetos"} · frete{" "}
                  {formatBRL(summary.freightCostCents)}
                </span>
              )}
              {list.hasNextPage && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => list.fetchNextPage()}
                >
                  Carregar mais
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
